import {
  AddIcon,
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  LazyLoader,
  Modal,
  Tooltip,
} from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import { getDaysDifference } from '../../utils/dateCalculations';
import { createEventsForDateRange } from '../../utils/eventsApi';
import { showSuccessDeleteToast, showUserErrorToast } from '../../utils/toast';
import { TimetableSubjectContainerForm } from './Forms/TimetableSubjectContainerForm';
import { getTodayPosition } from './getTodayPosition';
import { HolidaysBar } from './holidays/HolidaysBar';
import { useTimetableBlockClassConflicts } from './hooks/useTimetableBlockClassConflicts';
import { Months } from './months/Months';
import { SubjectContainer } from './SubjectContainer';
import styles from './TimetableBlock.module.scss';
import { TodayMarker } from './TodayMarker';
import { useTimetableBlock } from './useTimetableBlock';
import { Weeks } from './weeks/Weeks';

export const TimetableBlock = () => {
  const { t } = useTranslation();
  const permissions = usePermissionChecker();
  const schoolYear = useSelectedSchoolYear();
  const { pimAuthClaims } = useAuthClaims();
  const { deleteTimetableBlock, isLoading, processedBlocks, openModal, TimetableBlockModal } = useTimetableBlock();
  const classConflicts = useTimetableBlockClassConflicts();

  const [containerModalOpen, setContainerModalOpen] = useState<boolean>(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const schoolYearDays = getDaysDifference(schoolYear?.start, schoolYear?.end);

  const {
    started: todayStarted,
    inRange: todayInRange,
    percent: todayPercent,
  } = getTodayPosition(schoolYear?.start, schoolYear?.end);

  async function handleDelete(uuid: string, from: Date, to: Date) {
    const result = await deleteTimetableBlock(uuid);
    const _from = dayjs(from).startOf('day').format('YYYY-MM-DD');
    const _to = dayjs(to).endOf('day').format('YYYY-MM-DD');
    if (result.error) {
      showUserErrorToast({ error: result.error });
    } else {
      showSuccessDeleteToast();
      if (schoolYear) {
        await createEventsForDateRange({
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
          schoolYearUuid: schoolYear.uuid,
          from: _from,
          to: _to,
        });
      }
    }
  }

  const blockMenuData = (block: (typeof processedBlocks)[number]) => {
    const daysPassed = getDaysDifference(new Date(), block.end);
    return [
      {
        label: t('common.edit'),
        onClick: () => {
          openModal(block);
        },
      },
      {
        label: t('timetableBlock.divide'),
        disabled: true,
      },
      {
        label: t('timetableBlock.mergeWith'),
        disabled: true,
      },
      {
        label: t('common.delete'),
        type: 'default' as const,
        color: 'error' as const,
        onClick: () => handleDelete(block.uuid, block.start, block.end),
        disabled: daysPassed < block.days,
      },
    ];
  };

  const formatSpan = (start: Date, end: Date) => `${dayjs(start).format('DD.MM.')} – ${dayjs(end).format('DD.MM.')}`;

  // Länge inklusive Start- und Endtag; unter einer Woche in Tagen
  const formatDuration = (block: (typeof processedBlocks)[number]) => {
    const days = block.days + 1;
    return days < 7
      ? t('timetableBlock.days', { count: days })
      : t('timetableBlock.weeks', { count: Math.round(days / 7) });
  };

  return (
    <div className={styles['timetable-block']} ref={wrapperRef}>
      {isLoading && <LazyLoader className={styles.loader} embedded size='xxl' />}
      <div className={styles.timeline}>
        {/* Reihenfolge: Kalender, Ferien (direkt unter den KW), Stundenplanabschnitte, Epochenpläne */}
        <Months />
        <Weeks />
        <HolidaysBar />
        <div className={styles['sub-headline']}>{t('timetable.title', { count: 2 })}</div>
        <div className={styles.blocks}>
          {processedBlocks.map((block, index) => {
            // Breite inklusiv rechnen (wie schoolYearDays), damit sich die Blöcke exakt
            // auf 100 % summieren und der letzte Block bis zur Kante reicht. block.days
            // (exklusiv) bleibt für Layout-Schwellen/Lösch-Logik unverändert.
            const blockWidthPercent = (getDaysDifference(block.start, block.end) / schoolYearDays) * 100;

            const actionClasses = classNames(styles.actions, {
              [styles['actions-right']]: block.days >= 14,
            });

            const blockClasses = classNames(styles.block, {
              [styles['has-block']]: !block.empty,
              [styles['has-class-conflict']]: classConflicts.has(block.uuid),
            });

            return (
              <div key={index + block.uuid} className={blockClasses} style={{ flex: `0 1 ${blockWidthPercent}%` }}>
                <Tooltip
                  usePortal={true}
                  content={
                    <>
                      <div>{block.name ?? t('timetableBlock.title', { count: 1 })}</div>
                      <div>{`${dayjs(block.start).format('DD.MM.')} - ${dayjs(block.end).format('DD.MM.')}`}</div>
                      <div>{formatDuration(block)}</div>
                    </>
                  }
                >
                  {block.days >= 21 && (
                    <div className={styles.name}>{block.name ?? t('timetableBlock.title', { count: 1 })}</div>
                  )}
                  {block.days >= 14 && (
                    <div
                      className={styles['time-span']}
                    >{`${dayjs(block.start).format('DD.MM.')} - ${dayjs(block.end).format('DD.MM.')}`}</div>
                  )}
                  {block.days >= 14 && <div className={styles.duration}>{formatDuration(block)}</div>}
                  {block.days < 14 && <div className={styles.placeholder}></div>}
                </Tooltip>
                {classConflicts.has(block.uuid) && (
                  <Tooltip
                    usePortal={true}
                    triggerClass={classNames(styles['class-conflict'], {
                      [styles['class-conflict-beside-actions']]: block.days >= 14,
                    })}
                    content={t('timetableBlock.classConflict', { names: classConflicts.get(block.uuid)?.join(', ') })}
                  >
                    <span aria-hidden>!</span>
                  </Tooltip>
                )}
                {block.empty ? (
                  <Button
                    className={actionClasses}
                    icon={<AddIcon className='small' />}
                    hierarchy='ghost'
                    disabled={!permissions?.canWriteTimetable()}
                    onClick={() => {
                      openModal(block);
                    }}
                  />
                ) : (
                  <Dropdown
                    triggerClass={actionClasses}
                    trigger={
                      <Button
                        disabled={!permissions?.canWriteTimetable()}
                        icon={<DotsVerticalIcon className='small' />}
                        hierarchy='ghost'
                      />
                    }
                    disabled={!permissions?.canWriteTimetable()}
                  >
                    <DropdownMenu data={blockMenuData(block)} />
                  </Dropdown>
                )}
              </div>
            );
          })}
          {todayStarted && <TodayMarker percent={todayPercent} withFill showLine={todayInRange} />}
        </div>

        <SubjectContainer />
      </div>

      {/* Mobil: Abschnitte als Liste unter der Zeitleiste (mobile-plan.md E6, Variante B) */}
      <ul className={styles['block-list']}>
        {processedBlocks.map((block, index) => {
          return (
            <li key={index + block.uuid} className={classNames(styles.row, { [styles.empty]: block.empty })}>
              <span className={styles.dot} aria-hidden />
              <div className={styles.text}>
                <div className={styles.title}>
                  {block.empty ? t('timetableBlock.noBlock') : (block.name ?? t('timetableBlock.title', { count: 1 }))}
                </div>
                <div className={styles.meta}>
                  {formatSpan(block.start, block.end)} · {formatDuration(block)}
                </div>
                {classConflicts.has(block.uuid) && (
                  <div className={styles['class-conflict-text']}>
                    {t('timetableBlock.classConflict', { names: classConflicts.get(block.uuid)?.join(', ') })}
                  </div>
                )}
              </div>
              {block.empty ? (
                <Button
                  icon={<AddIcon />}
                  hierarchy='tertiary'
                  disabled={!permissions?.canWriteTimetable()}
                  title={t('timetableBlock.addBlock')}
                  onClick={() => openModal(block)}
                />
              ) : (
                <Dropdown
                  trigger={
                    <Button
                      disabled={!permissions?.canWriteTimetable()}
                      icon={<DotsVerticalIcon />}
                      hierarchy='tertiary'
                    />
                  }
                  disabled={!permissions?.canWriteTimetable()}
                >
                  <DropdownMenu data={blockMenuData(block)} />
                </Dropdown>
              )}
            </li>
          );
        })}
      </ul>

      <SubjectContainer variant='list' />

      <TimetableBlockModal />

      <Modal
        title={t('subjectContainer.add')}
        isOpen={containerModalOpen}
        onRequestClose={() => setContainerModalOpen(false)}
      >
        <TimetableSubjectContainerForm onClose={() => setContainerModalOpen(false)} />
      </Modal>
    </div>
  );
};
